import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Heart, Car as GarageIcon, Bell, User, LogOut, LayoutDashboard, Wrench } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { useMarket } from "@/context/MarketContext";
import { translations } from "@/i18n/translations";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { FLAGS } from "@/lib/format";

const LANGS = [
  { code: "sk", label: "SK" }, { code: "cs", label: "CZ" }, { code: "pl", label: "PL" },
  { code: "hu", label: "HU" }, { code: "en", label: "EN" },
];

function Logo() {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-2">
      <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary text-white">
        <Wrench className="h-5 w-5" />
      </span>
      <span className="font-heading text-xl font-bold tracking-tighter text-slate-900">
        PartFinder<span className="text-primary">.sk</span>
      </span>
    </Link>
  );
}

function Selectors() {
  const { lang, setLang } = useI18n();
  const { market, setMarket, markets } = useMarket();
  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger data-testid="market-selector" className="flex items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-200 hover:border-primary/40">
          <span>{FLAGS[market]}</span><span>{market}</span><ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {markets.map((m) => (
            <DropdownMenuItem key={m.code} data-testid={`market-${m.code}`} onClick={() => setMarket(m.code)}>
              <span className="mr-2">{FLAGS[m.code]}</span>{m.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger data-testid="lang-selector" className="flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors duration-200 hover:border-primary/40">
          {LANGS.find((l) => l.code === lang)?.label || "EN"}<ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {LANGS.map((l) => (
            <DropdownMenuItem key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)}>
              {translations[l.code]?.["nav.about"] ? l.label : l.label} — {l.code.toUpperCase()}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function AccountMenu() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="flex items-center gap-2">
        <Link to="/login" data-testid="nav-login" className="rounded-full px-4 py-2 text-sm font-medium text-slate-700 transition-colors duration-200 hover:text-primary">
          {t("nav.login")}
        </Link>
        <Link to="/register" data-testid="nav-signup" className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-blue-600">
          {t("nav.signup")}
        </Link>
      </div>
    );
  }

  const items = [
    { to: "/garage", icon: GarageIcon, label: t("nav.garage") },
    { to: "/favorites", icon: Heart, label: t("nav.favorites") },
    { to: "/alerts", icon: Bell, label: t("nav.alerts") },
    { to: "/profile", icon: User, label: t("nav.profile") },
    { to: "/partner/dashboard", icon: LayoutDashboard, label: t("nav.dashboard") },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="account-menu" className="flex items-center gap-2 rounded-full border border-border bg-white px-2 py-1.5 pr-3 text-sm font-medium text-slate-700 transition-colors duration-200 hover:border-primary/40">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-primary text-xs font-bold text-white">
          {user.name?.[0]?.toUpperCase() || "U"}
        </span>
        <span className="hidden sm:inline max-w-[100px] truncate">{user.name}</span>
        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {items.map((i) => (
          <DropdownMenuItem key={i.to} data-testid={`menu-${i.to.replace(/\//g, "-")}`} onClick={() => navigate(i.to)}>
            <i.icon className="mr-2 h-4 w-4" />{i.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }}>
          <LogOut className="mr-2 h-4 w-4" />{t("nav.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Header() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const nav = [
    { to: "/search", label: t("nav.categories") },
    { to: "/how-it-works", label: t("nav.how") },
    { to: "/partner", label: t("nav.partners") },
    { to: "/about", label: t("nav.about") },
  ];
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-8">
          <Logo />
          <nav className="hidden items-center gap-6 lg:flex">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-200 hover:text-primary ${isActive ? "text-primary" : "text-slate-600"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="hidden items-center gap-3 lg:flex">
          <Selectors />
          <AccountMenu />
        </div>
        <button data-testid="mobile-menu-btn" className="lg:hidden" onClick={() => setOpen((o) => !o)}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-border bg-white px-4 py-4 lg:hidden">
          <nav className="flex flex-col gap-3">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className="text-sm font-medium text-slate-700">
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-4 flex items-center justify-between">
            <Selectors />
            <AccountMenu />
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { t } = useI18n();
  const cols = [
    { title: t("footer.company"), links: [["/about", t("nav.about")], ["/partner", t("nav.partners")], ["/contact", "Contact"]] },
    { title: t("footer.resources"), links: [["/how-it-works", t("nav.how")], ["/methodology", "Methodology"], ["/faq", "FAQ"]] },
    { title: t("footer.legal"), links: [["/privacy", "Privacy"], ["/terms", "Terms"], ["/cookies", "Cookies"]] },
  ];
  return (
    <footer className="border-t border-border bg-slate-900 text-slate-300">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:grid-cols-4 lg:px-8">
        <div className="md:col-span-1">
          <span className="font-heading text-xl font-bold tracking-tighter text-white">PartFinder<span className="text-blue-400">.sk</span></span>
          <p className="mt-3 max-w-xs text-sm text-slate-400">{t("footer.tagline")}</p>
          <div className="mt-4 flex gap-2">
            {Object.entries(FLAGS).map(([c, f]) => <span key={c} title={c}>{f}</span>)}
          </div>
        </div>
        {cols.map((col) => (
          <div key={col.title}>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{col.title}</h4>
            <ul className="mt-4 space-y-2.5">
              {col.links.map(([to, label]) => (
                <li key={to}>
                  <Link to={to} className="text-sm text-slate-400 transition-colors duration-200 hover:text-white">{label}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-800 py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} PartFinder.sk — {t("footer.rights")}
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="flex min-h-screen flex-col bg-[#F8F9FA]">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
